import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FloppyDisk, BookOpen, Plus, MapPin, Clock } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/text-area';
import { Label } from '../components/ui/label';
import { ConfirmModal } from '../components/ui/confirm-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { cn } from '../lib/utils';
import { Switch } from '../components/ui/switch';
import { useLocations } from '../hooks/use-locations';
import { runAutomaticConflictDetection } from '../utils/conflictDetection';
import { getUserAccountId } from '../utils/accountUtils';
import BrainSettingConditions from '../components/brain/BrainSettingConditions'; // Import the new component
import LocationVariantsModal from '../components/LocationVariantsModal'; // Import the new component

// Skeleton component for loading state
const SimpleAnswerSkeleton = () => {
  return (
    <div className="space-y-5 sm:space-y-6 py-4 sm:py-5 animate-pulse">
      {/* Title Skeleton */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Content Skeleton */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="h-40 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Category Skeleton */}
      <div className="space-y-2 pt-3 border-t border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-1/5"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  );
};

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

const modalVariants = {
  hidden: { scale: 0.95, opacity: 0, y: 20 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    y: 0, 
    transition: { 
      type: "spring", 
      damping: 30, 
      stiffness: 300,
      duration: 0.4 
    }
  },
  exit: { 
    scale: 0.98,
    opacity: 0,
    y: 10,
    transition: { duration: 0.2 }
  }
};

const SimpleAnswerModal = ({ isOpen, onClose, onSave, answer = null, isInline = false, isPreLoaded = false, preSelectedCategoryId = null, onUpdateSetting, onCreateCategory, newlyCreatedCategory, onClearNewlyCreatedCategory, onOpenActivationConditions, setting }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category_id: '',
    showLocationVariants: false,
  });
  const [requirements, setRequirements] = useState([]); // Add state for requirements
  const [accountId, setAccountId] = useState(null); // Add state for accountId

  const [categories, setCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalIsLoading, setModalIsLoading] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({
    question: '',
    answer: '',
    category_id: '',
    showLocationVariants: false,
  });
  const titleRef = useRef(null);
  const modalRef = useRef(null);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Account branding data
  const [accountData, setAccountData] = useState({
    companyName: '',
    businessTypeFriendly: '',
    updatedAt: null
  });

  // Fetch locations for variants
  const { locations: fetchedLocations, loading: locationsLoading } = useLocations(businessType);

  // Location variant states
  const [locationGroupings, setLocationGroupings] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState({});
  const [activeLocationGrouping, setActiveLocationGrouping] = useState(null);
  const [formDataByGrouping, setFormDataByGrouping] = useState({});
  const [hasLocationChanges, setHasLocationChanges] = useState(false);
  const [hasActiveVariants, setHasActiveVariants] = useState(false);

  // Function to check if there are actual changes
  const checkForActualChanges = (currentFormData, currentFormDataByGrouping = {}) => {
    // For new documents, any content means there are changes
    if (!formData.settings_id) {
      return currentFormData.question.trim() !== '' || currentFormData.answer.trim() !== '' || currentFormData.category_id !== '';
    }

    // For existing documents, compare with original values
    const hasBasicChanges = (
      currentFormData.question.trim() !== originalFormData.question.trim() ||
      currentFormData.answer.trim() !== originalFormData.answer.trim() ||
      currentFormData.category_id !== originalFormData.category_id
    );

    // Also check location changes if variants are enabled
    return hasBasicChanges || hasLocationChanges;
  };

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to close modal (for non-inline mode)
  useEffect(() => {
    if (!isInline && isOpen) {
      const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          handleCloseAttempt();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, isInline]);

  // Fetch the most recent updated_at from brain_simple_answers table
  const fetchLastUpdatedTimestamp = async (settingsId) => {
    try {
      if (!settingsId) return null;

      const { data, error } = await supabase
        .from('brain_simple_answers')
        .select('updated_at')
        .eq('settings_id', settingsId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching last updated timestamp:', error);
        return null;
      }

      return data?.updated_at || null;
    } catch (error) {
      console.error('Error fetching last updated timestamp:', error);
      return null;
    }
  };

  // Fetch account branding data
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!isOpen) return;

      try {
        const { accountId: userAccountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError || !userAccountId) return;
        setAccountId(userAccountId); // Set accountId state

        const [accountResponse, businessTypeResponse] = await Promise.all([
          supabase
            .from('accounts')
            .select('company_name')
            .eq('id', userAccountId)
            .single(),
          supabase
            .from('system_business_types')
            .select('friendly_name')
            .eq('name', businessType)
            .single()
        ]);

        if (accountResponse.data) {
          setAccountData({
            companyName: accountResponse.data.company_name || '',
            businessTypeFriendly: businessTypeResponse.data?.friendly_name || businessType
          });
        }
      } catch (error) {
        console.error('Error fetching account data:', error);
      }
    };

    fetchAccountData();
  }, [isOpen, businessType, supabase]);

  // Fetch categories when the modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Fetch categories from the database
  const fetchCategories = async () => {
    try {
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;
      if (!accountId) return;

      // Fetch categories for the current account
      const { data, error } = await supabase
        .from('brain_categories')
        .select('id, name')
        .eq('account_id', accountId)
        .order('name');

      if (error) throw error;

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  // Initialize form with data if editing
  useEffect(() => {
    const loadInitialData = async () => {
      if (isOpen) {
        // Reset common states
        setValidationErrors({});
        setHasUnsavedChanges(false);
        setHasLocationChanges(false);
        setRequirements([]); // Reset requirements

        // OPTIMIZATION: Skip loading states when data is pre-loaded
        setModalIsLoading(!isPreLoaded);

        if (answer && answer.settings_id) {
          const settingsId = answer.settings_id;

          // Only fetch requirements for real database IDs (not temporary IDs)
          const isRealDatabaseId = !settingsId.toString().startsWith('new-') && 
                                   !settingsId.toString().startsWith('edit-') && 
                                   !settingsId.toString().startsWith('add-');

          if (isRealDatabaseId) {
            // Fetch requirements for the setting
            const { data: reqData, error: reqError } = await supabase
              .from('brain_setting_requirements')
              .select(`
                *,
                brain_conversation_topics (
                  topic_name
                )
              `)
              .eq('settings_id', settingsId);

            if (reqError) {
              console.error("Error fetching requirements", reqError);
            } else {
              setRequirements(reqData || []);
            }
          } else {
            // For new items, start with empty requirements
            setRequirements([]);
          }

          const fetchedGroupings = answer.locationGroupings || [];
          const fetchedAssignments = answer.locationAssignments || {};
          const fetchedFormDataByGrouping = answer.formDataByGrouping || {};

          // NEW: Determine if there are active variants based on assignments
          const hasAssignments = Object.values(fetchedAssignments).some(locs => locs && locs.length > 0);
          setHasActiveVariants(hasAssignments);


          const firstGroupingIdIfAny = fetchedGroupings.length > 0 ? fetchedGroupings[0].id : null;
          // Check for grouping_id first (for variants), then mainGroupingId (for settings), then fallback to first grouping
          const initialActiveGroupingId = answer.grouping_id || answer.mainGroupingId || firstGroupingIdIfAny;

          // The content of the answer is variant-specific
          const initialAnswerContent = fetchedFormDataByGrouping[initialActiveGroupingId]?.answer || answer.answer || '';

          const initialFormData = {
            settings_id: answer.settings_id,
            category_id: answer.category_id,
            question: answer.name || '', // Use the invariant setting name for the title field
            answer: initialAnswerContent,
            showLocationVariants: answer.showLocationVariants || false,
          };

          setFormData(initialFormData);

          // Store original values for comparison
          setOriginalFormData({
            question: answer.name || '',
            answer: initialAnswerContent,
            category_id: answer.category_id,
            showLocationVariants: answer.showLocationVariants || false,
          });

          setLocationGroupings(fetchedGroupings);
          setLocationAssignments(fetchedAssignments);
          setFormDataByGrouping(fetchedFormDataByGrouping);
          setActiveLocationGrouping(initialActiveGroupingId);

          // Fetch the actual updated_at from brain_simple_answers table
          if (isRealDatabaseId) {
            const lastUpdatedTimestamp = await fetchLastUpdatedTimestamp(settingsId);
            setAccountData(prev => ({
              ...prev,
              updatedAt: lastUpdatedTimestamp
            }));
          } else {
            setAccountData(prev => ({
              ...prev,
              updatedAt: null
            }));
          }

          // OPTIMIZATION: Instantly set loading to false when pre-loaded
          if (isPreLoaded) {
            setModalIsLoading(false);
          } else {
            setModalIsLoading(false);
          }

          setTimeout(() => {
            titleRef.current?.focus();
          }, 100);

        } else {
          const newFormData = { 
            question: '', 
            answer: '', 
            category_id: preSelectedCategoryId || '', 
            showLocationVariants: false, 
            settings_id: null 
          };

          setFormData(newFormData);

          // Store original values for new documents (empty)
          setOriginalFormData({
            question: '',
            answer: '',
            category_id: preSelectedCategoryId || '',
            showLocationVariants: false,
          });

          setLocationGroupings([]);
          setLocationAssignments({});
          setFormDataByGrouping({});
          setActiveLocationGrouping(null);

          // Reset updated_at for new records
          setAccountData(prev => ({
            ...prev,
            updatedAt: null
          }));

          // OPTIMIZATION: Instantly set loading to false when pre-loaded or new
          setModalIsLoading(false);
          setTimeout(() => {
            titleRef.current?.focus();
          }, 100);
        }
      }
    };

    loadInitialData();
  }, [isOpen, answer, isPreLoaded, preSelectedCategoryId]);

  // Add voice form filling event listener
  useEffect(() => {
    if (!isOpen) return;

    const handleVoiceFormFill = (event) => {
      const { fieldName, value, modalType } = event.detail;

      // Only handle events for this modal type or universal events
      if (modalType && modalType !== 'simple_answer') return;

      // Map voice field names to form fields
      const fieldMapping = {
        'title': 'question',
        'question': 'question', 
        'name': 'question',
        'section title': 'question',
        'content': 'answer',
        'answer': 'answer',
        'description': 'answer',
        'text': 'answer',
        'section content': 'answer'
      };

      const normalizedFieldName = fieldName.toLowerCase().trim();
      const actualFieldName = fieldMapping[normalizedFieldName] || normalizedFieldName;

      // Update the appropriate field
      if (actualFieldName === 'question' || actualFieldName === 'answer') {
        if (formData.showLocationVariants) {
          // Handle location variants
          handleFormChangeWithVariants({ 
            target: { name: actualFieldName, value: value }
          });
        } else {
          // Handle regular form
          const newFormData = {
            ...formData,
            [actualFieldName]: value
          };

          setFormData(newFormData);

          // Check if there are actual changes
          setHasUnsavedChanges(checkForActualChanges(newFormData));

          // Clear validation error
          if (validationErrors[actualFieldName]) {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[actualFieldName];
              return newErrors;
            });
          }
        }
      } else {
        console.warn('Unknown field name for Simple Answer:', fieldName);
      }
    };

    window.addEventListener('fillFormField', handleVoiceFormFill);

    return () => {
      window.removeEventListener('fillFormField', handleVoiceFormFill);
    };
  }, [isOpen, formData.showLocationVariants, validationErrors]);

  // Auto-select newly created category
  useEffect(() => {
    if (newlyCreatedCategory && isOpen) {
      // Add the category to the local list if it's not already there
      setCategories(prev => {
        const exists = prev.some(cat => cat.id === newlyCreatedCategory.id);
        if (!exists) {
          return [...prev, newlyCreatedCategory];
        }
        return prev;
      });

      // Select the new category
      const newFormData = {
        ...formData,
        category_id: newlyCreatedCategory.id
      };

      setFormData(newFormData);

      // Clear validation error for category
      if (validationErrors.category_id) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[category_id];
          return newErrors;
        });
      }

      // Check if there are actual changes
      setHasUnsavedChanges(checkForActualChanges(newFormData));

      // Clear the newly created category after using it
      if (onClearNewlyCreatedCategory) {
        onClearNewlyCreatedCategory();
      }
    }
  }, [newlyCreatedCategory, isOpen, onClearNewlyCreatedCategory, validationErrors.category_id]);

  // Handle field changes
  const handleChange = (e) => {
    // Defensive check to ensure we only get plain values
    const target = e.target;
    if (!target || typeof target.name !== 'string' || typeof target.value !== 'string') {
      console.warn('handleChange received invalid target:', target);
      return;
    }

    const { name, value } = target;

    const newFormData = {
      ...formData,
      [name]: String(value || '') // Ensure value is always a string
    };

    setFormData(newFormData);

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Check if there are actual changes
    setHasUnsavedChanges(checkForActualChanges(newFormData));
  };

  // Handle category selection
  const handleCategoryChange = (value) => {
    if (value === 'new') {
      if (onCreateCategory) {
        onCreateCategory();
      }
      return;
    }

    const newFormData = {
      ...formData,
      category_id: value
    };

    setFormData(newFormData);

    // Clear validation error for category
    if (validationErrors.category_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.category_id;
        return newErrors;
      });
    }

    // Check if there actual changes
    setHasUnsavedChanges(checkForActualChanges(newFormData));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.question.trim()) {
      errors.question = "Title is required";
    }

    if (!formData.answer.trim()) {
      errors.answer = "Content is required";
    }

    if (!formData.category_id) {
      errors.category_id = "Category is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle variant-specific field changes
  const handleFormChangeWithVariants = (e, groupingId = activeLocationGrouping) => {
    // Defensive check to ensure we only get plain values
    const target = e.target;
    if (!target || typeof target.name !== 'string' || typeof target.value !== 'string') {
      console.warn('handleFormChangeWithVariants received invalid target:', target);
      return;
    }

    const { name, value } = target;

    // 1) Stash variant value
    const newFormDataByGrouping = {
      ...formDataByGrouping,
      [groupingId]: {
        ...(formDataByGrouping[groupingId] || {}),
        [name]: String(value || '') // Ensure value is always a string
      }
    };

    setFormDataByGrouping(newFormDataByGrouping);

    // 2) Keep main form in sync for immediate UI feedback
    const newFormData = {
      ...formData,
      [name]: String(value || '') // Ensure value is always a string
    };

    setFormData(newFormData);

    // 3) Clear validation error if any
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const ne = { ...prev };
        delete ne[name];
        return ne;
      });
    }

    // Check if there are actual changes
    setHasUnsavedChanges(checkForActualChanges(newFormData, newFormDataByGrouping));
  };

  // Sync form fields when switching grouping
  useEffect(() => {
    if (formData.showLocationVariants && activeLocationGrouping) {
      // Seed blank grouping stash if not present
      if (!formDataByGrouping[activeLocationGrouping]) {
        setFormDataByGrouping(prev => ({
          ...prev,
          [activeLocationGrouping]: { question: '', answer: '' }
        }));
        setFormData(prev => ({ ...prev, answer: '' }));
        return;
      }
      const grpFields = formDataByGrouping[activeLocationGrouping];
      // Only update the answer content, keep the question (setting name) consistent
      setFormData(prev => ({
        ...prev,
        answer: grpFields.answer ?? ''
      }));
    }
  }, [activeLocationGrouping]);

  // Handle data from LocationVariantsSidePanel
  const handleLocationVariantsChange = (data) => {
    setLocationGroupings(data.groupings);
    setLocationAssignments(data.assignedLocations);
    // NEW: Update active variants state after modal save
    const hasAssignments = Object.values(data.assignedLocations).some(locs => locs && locs.length > 0);
    setHasActiveVariants(hasAssignments);
    setFormData(prev => ({ ...prev, showLocationVariants: hasAssignments }));

    if (data.selectedGrouping) {
      setActiveLocationGrouping(data.selectedGrouping);
      if (!formDataByGrouping[data.selectedGrouping]) {
        setFormDataByGrouping(prev => ({
          ...prev,
          [data.selectedGrouping]: { question: '', answer: '' }
        }));
      }
    }
    setHasLocationChanges(true);
    setFormData(prev => ({ ...prev, showLocationVariants: data.groupings.length > 1 }));
    setHasUnsavedChanges(true);
  };

  //////////////////////////////
  // createNewAnswer helper   //
  //////////////////////////////
  const createNewAnswer = async () => {
    // Sanitize form data to ensure only plain strings are used
    const sanitizedFormData = {
      category_id: String(formData.category_id || ''),
      question: String(formData.question || ''),
      answer: String(formData.answer || '')
    };

    // 1. brain_settings row
    const { data: settingsRow, error: settingsErr } = await supabase
      .from('brain_settings')
      .insert({
        category_id: sanitizedFormData.category_id,
        type: 'simple_answer',
        name: sanitizedFormData.question, // The name is the question
        description: null,
        triggers: null,
        rules: null
      })
      .select()
      .single();
    if (settingsErr) throw settingsErr;
    const settingsId = settingsRow.id;

    // Determine groupings
    let groupsToSave = [];
    if (formData.showLocationVariants) groupsToSave = locationGroupings.length ? [...locationGroupings] : [];
    if (!groupsToSave.length) groupsToSave = [{ id: 'default', name: 'Default' }];

    // Map temp -> db id
    const groupingIdMap = {};

    // 2. Insert groupings
    for (const grp of groupsToSave) {
      const { data: grpRow, error: grpErr } = await supabase
        .from('brain_groupings')
        .insert({ settings_id: settingsId, name: String(grp.name || 'Default') })
        .select()
        .single();
      if (grpErr) throw grpErr;
      groupingIdMap[grp.id] = grpRow.id;
    }

    // 3. Insert grouping_locations
    const locRecords = [];
    for (const grp of groupsToSave) {
      const locIds = formData.showLocationVariants ? (locationAssignments[grp.id] || []) : [];
      locIds.forEach((locId) => {
        locRecords.push({ grouping_id: groupingIdMap[grp.id], location_id: locId });
      });
    }
    if (locRecords.length) {
      const { error: locErr } = await supabase.from('brain_grouping_locations').insert(locRecords);
      if (locErr) throw locErr;
    }

    // 4. Insert simple answers rows
    for (const grp of groupsToSave) {
      const grpData = formData.showLocationVariants && formDataByGrouping[grp.id] ? formDataByGrouping[grp.id] : sanitizedFormData;

      // Sanitize group data as well
      const sanitizedGrpData = {
        question: String(grpData.question || ''),
        answer: String(grpData.answer || '')
      };

      await supabase.from('brain_simple_answers').insert({
        settings_id: settingsId,
        grouping_id: groupingIdMap[grp.id],
        title: sanitizedFormData.question, // Title now mirrors the setting name
        content: sanitizedGrpData.answer
      });
    }

    // 5. Return the formatted setting data that matches fetchCategories structure (with sanitized data)
    const formattedSetting = {
      ...settingsRow,
      original_type: settingsRow.type,
      fullData: {
        id: null, // Simple answer row ID would go here, but not needed for sidebar display
        settings_id: settingsId,
        name: settingsRow.name,
        question: sanitizedFormData.question,
        answer: sanitizedFormData.answer,
        category_id: settingsRow.category_id,
      },
      answer_content: sanitizedFormData.answer, // For conflict detection
      answer_title: sanitizedFormData.question, // For conflict detection
      locationVariants: [] // Start with empty variants, will be populated when fetched
    };

    return formattedSetting;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please complete all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if this is a real database ID or a temporary ID
      const isExistingRecord = answer && answer.settings_id && !answer.settings_id.toString().startsWith('add-') && !answer.settings_id.toString().startsWith('edit-') && !answer.settings_id.toString().startsWith('new-');

      let settingsId;

      if (isExistingRecord) {
        // UPDATE Logic
        settingsId = answer.settings_id;

        // Sanitize form data to ensure only plain strings are used
        const sanitizedFormData = {
          question: String(formData.question || ''),
          answer: String(formData.answer || ''),
          category_id: String(formData.category_id || '')
        };

        // 1. Update brain_settings name and category
        await supabase
          .from('brain_settings')
          .update({ name: sanitizedFormData.question, category_id: sanitizedFormData.category_id })
          .eq('id', settingsId);

        // Always upsert the simple answer record for the active grouping.
        // This handles both updating existing variants and creating new ones for an existing setting.
        if (activeLocationGrouping) {
          const { error: upsertError } = await supabase
            .from('brain_simple_answers')
            .upsert({
              settings_id: settingsId,
              grouping_id: activeLocationGrouping,
              title: sanitizedFormData.question, // Keep title in sync with setting name
              content: sanitizedFormData.answer
            }, {
              onConflict: 'settings_id,grouping_id' // Use the unique constraint for conflict resolution
            });

          if (upsertError) throw upsertError;
        } else {
          // This block handles a non-variant setting. We need to find or create its default grouping.
          const { data: defaultGroup, error: groupError } = await supabase
            .from('brain_groupings')
            .select('id')
            .eq('settings_id', settingsId)
            .limit(1)
            .maybeSingle();

          let defaultGroupingId;
          if (groupError && groupError.code !== 'PGRST116') throw groupError; // Handle actual errors

          if (defaultGroup) {
            defaultGroupingId = defaultGroup.id;
          } else {
            // If no groupings exist, create a default one.
            const { data: newGroup, error: newGroupError } = await supabase
              .from('brain_groupings')
              .insert({ settings_id: settingsId, name: 'Default' })
              .select('id')
              .single();
            if (newGroupError) throw newGroupError;
            defaultGroupingId = newGroup.id;
          }

          const { error: upsertError } = await supabase
            .from('brain_simple_answers')
            .upsert({
              settings_id: settingsId,
              grouping_id: defaultGroupingId,
              title: sanitizedFormData.question, // Keep title in sync with setting name
              content: sanitizedFormData.answer
            }, {
              onConflict: 'settings_id,grouping_id'
            });

          if (upsertError) throw upsertError;
        }

      } else {
        // CREATE Logic (already handled by createNewAnswer)
        const newSettingData = await createNewAnswer();
        settingsId = newSettingData.id;
        toast.success("Simple answer created");

        // Refresh the timestamp after successful creation
        const refreshedTimestamp = await fetchLastUpdatedTimestamp(settingsId);
        setAccountData(prev => ({
          ...prev,
          updatedAt: refreshedTimestamp
        }));

        // Run automatic conflict detection after successful creation
        runAutomaticConflictDetection(supabase);

        // Pass structured result for coverage analysis
        const createResult = { success: true, settingsId: newSettingData.id, isCreate: true, data: newSettingData };
        onClose(createResult);
      }

      // SAVE REQUIREMENTS (for both create and update)
      if (settingsId) {
        // 1. Delete all existing requirements for this setting
        const { error: deleteError } = await supabase
          .from('brain_setting_requirements')
          .delete()
          .eq('settings_id', settingsId);

        if (deleteError) {
          throw new Error(`Failed to update requirements: ${deleteError.message}`);
        }

        // 2. Insert the new requirements if there are any
        if (requirements.length > 0) {
          const requirementsToInsert = requirements.map(req => ({
            settings_id: settingsId,
            topic_id: req.topic_id,
            required_answer: req.required_answer,
          }));

          const { error: insertError } = await supabase
            .from('brain_setting_requirements')
            .insert(requirementsToInsert);

          if (insertError) {
            throw new Error(`Failed to save new requirements: ${insertError.message}`);
          }
        }
      }

      if (isExistingRecord) {
        toast.success("Simple answer updated");

        // **OPTIMISTIC UI UPDATE**: Update parent categories state immediately
        if (onUpdateSetting) {
          onUpdateSetting(settingsId, {
            name: formData.question,
            category_id: formData.category_id
          });
        }

        // Refresh the timestamp after successful update
        const refreshedTimestamp = await fetchLastUpdatedTimestamp(settingsId);
        setAccountData(prev => ({
          ...prev,
          updatedAt: refreshedTimestamp
        }));

        // Run automatic conflict detection after successful update
        runAutomaticConflictDetection(supabase);

        // Pass the settings ID for coverage analysis
        const updateResult = { success: true, settingsId: settingsId, isUpdate: true };
        onClose(updateResult); // Pass settings ID to trigger coverage analysis
      }

      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
      setHasLocationChanges(false);
    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error("Failed to save");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close with unsaved changes check
  const handleCloseAttempt = () => {
    if (isInline) {
      onClose('cancel');
      return;
    }
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onClose('cancel');
    }
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  // Chrome-style tab component for location variants
  const LocationVariantTabs = () => {

    if (!hasActiveVariants) {
      return null;
    }

    return (
      <div className="flex items-center bg-gray-50 border-b border-gray-200 px-6 py-0 overflow-x-auto">
        <div className="flex space-x-1 min-w-0">
          {locationGroupings.map((grouping, index) => {
            const isActive = activeLocationGrouping === grouping.id;
            return (
              <button
                key={grouping.id}
                onClick={() => setActiveLocationGrouping(grouping.id)}
                className={cn(
                  "relative px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 border-b-2 border-transparent",
                  "hover:text-gray-900 hover:bg-white/50",
                  isActive 
                    ? "text-blue-600 bg-white border-blue-600 shadow-sm" 
                    : "text-gray-600 hover:text-gray-800"
                )}
                style={{
                  borderRadius: isActive ? '8px 8px 0 0' : '8px 8px 0 0',
                  marginBottom: isActive ? '-1px' : '0'
                }}
              >
                <div className="flex items-center space-x-2">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-24">{grouping.name}</span>
                </div>
                {isActive && (
                  <div className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600"></div>
                )}
              </button>
            );
          })}
          {/* Add New Variant Button */}
          <button
            onClick={() => setIsLocationModalOpen(true)}
            className="flex items-center justify-center px-3 text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 rounded-t-lg transition-colors"
            title="Add new location version"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <motion.div 
      ref={modalRef}
      className={cn(
        "bg-white flex flex-col overflow-hidden",
        isInline 
          ? "w-full h-full" 
          : "rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] border border-gray-200/50"
      )}
      data-tutorial-target="simple-answer-modal"
      variants={!isInline ? modalVariants : {}}
      initial={!isInline ? "hidden" : ""}
      animate={!isInline ? "visible" : ""}
      exit={!isInline ? "exit" : ""}
      onClick={e => e.stopPropagation()}
    >
      {isInline ? (
        // 🎨 Inline mode - Microsoft Word document style with branding
        <div className="h-full bg-gradient-to-br from-blue-50/30 via-gray-50/20 to-blue-50/40 flex flex-col">
          {/* 🏢 BRANDED COMPANY LETTERHEAD WITH TITLE */}
          <motion.div 
            className="bg-white border-b border-gray-100/50 py-3 px-6 sm:px-8 relative"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            {/* Brand Stripe */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600"></div>

            {/* Company Letterhead */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Company Logo/Icon */}
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <BookOpen size={16} className="text-white" />
                  </div>
                  <div>
                    {/* Document Title in Header */}
                    <input
                      id="question"
                      name="question"
                      ref={titleRef}
                      value={formData.question}
                      onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                      placeholder="Untitled document"
                      className={cn(
                        "font-medium text-base sm:text-lg text-gray-900 placeholder-gray-400 bg-transparent border-0",
                        "focus:outline-none focus:ring-0 px-0 py-1 transition-colors w-full min-w-[300px] max-w-[500px]",
                        "hover:bg-gray-50/50 focus:bg-gray-50/50 rounded-md px-2",
                        validationErrors.question && "text-red-700 placeholder-red-300"
                      )}
                      style={{ 
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        lineHeight: '1.3'
                      }}
                      disabled={isSubmitting}
                    />
                    {/* Category Selection below title */}
                    <Select 
                      value={formData.category_id} 
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-6 text-[10px] sm:text-xs text-gray-500 border-0 bg-transparent hover:bg-gray-50 focus:bg-gray-50 rounded-md px-2 py-1 mt-0.5",
                          "focus:ring-0 focus:outline-none shadow-none w-full min-w-0",
                          validationErrors.category_id && "text-red-500"
                        )}
                      >
                        <SelectValue placeholder="Choose category..." />
                      </SelectTrigger>
                      <SelectContent className="z-[10060] bg-white border border-gray-200 rounded-lg shadow-lg">
                        {categories.map(category => (
                          <SelectItem 
                            key={category.id} 
                            value={category.id} 
                            className="text-sm py-2.5 hover:bg-gray-50 focus:bg-blue-50 cursor-pointer"
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="new" className="text-sm text-blue-600 py-2.5 hover:bg-blue-50 focus:bg-blue-100 cursor-pointer border-t border-gray-100">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-lg bg-blue-500 flex items-center justify-center mr-2">
                              <Plus className="h-2.5 w-2.5 text-white" />
                            </div>
                            Create new category
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {validationErrors.category_id && (
                      <div className="text-xs text-red-500 mt-1">
                        {validationErrors.category_id}
                      </div>
                    )}
                    {validationErrors.question && (
                      <div className="text-xs text-red-500 mt-1">
                        {validationErrors.question}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Badge and Action - Responsive */}
              <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                {/* Action buttons - Conditions first, then Save */}
                <div className="flex items-center space-x-2">
                  {/* Location Variants Button - NOW conditional on active variants */}
                  {!hasActiveVariants && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsLocationModalOpen(true)}
                      className="text-gray-700 border-gray-200 hover:bg-gray-50"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Location Variants
                    </Button>
                  )}
                  {onOpenActivationConditions && setting && (
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenActivationConditions(setting)}
                        className="text-gray-700 border-gray-200 hover:bg-gray-50"
                     >
                       <Clock className="w-4 h-4 mr-2" />
                       Conditions
                     </Button>
                  )}
                  <motion.button
                    whileHover={{ scale: hasUnsavedChanges || !formData.settings_id ? 1.02 : 1 }}
                    whileTap={{ scale: hasUnsavedChanges || !formData.settings_id ? 0.98 : 1 }}
                    onClick={handleSubmit}
                    disabled={isSubmitting || (formData.settings_id && !hasUnsavedChanges)}
                    className={cn(
                      "py-1.5 px-3 sm:py-2 sm:px-5 rounded-lg text-xs sm:text-sm font-medium transition-colors",
                      (hasUnsavedChanges || !formData.settings_id) 
                        ? "bg-blue-500 hover:bg-blue-600 text-white" 
                        : "bg-gray-200 text-gray-500 cursor-not-allowed",
                      "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
                        />
                        <span className="hidden sm:inline">Saving...</span>
                        <span className="sm:hidden">Save</span>
                      </div>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Save</span>
                        <span className="sm:hidden">Save</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Header Separator */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200"></div>
          </motion.div>

          {/* Chrome-style Location Variant Tabs - NOW conditional on active variants */}
          {hasActiveVariants && <LocationVariantTabs />}

          {/* Document Paper Container */}
          <div className="flex-1 overflow-y-auto">
            {/* Loading Skeleton */}
            {modalIsLoading && (
              <motion.div 
                className="flex items-center justify-center py-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Loading document...</p>
                </div>
              </motion.div>
            )}

            {/* Document Sheet */}
            {!modalIsLoading && (
              <motion.div 
                className="min-h-full py-8 px-4 flex justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {/* Paper Sheet */}
                <div className="w-full max-w-4xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.08)] rounded-sm border border-gray-200/50 overflow-hidden">
                  {/* Document Content */}
                  <div className="px-4 sm:px-8 md:px-12 py-6 sm:py-8 md:py-10 space-y-4 sm:space-y-6 md:space-y-8">
                    {/* Document Content */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                        <label className="text-sm font-light text-gray-700">
                          Content <span className="text-red-400 ml-1">*</span>
                        </label>
                      </div>
                      <textarea
                        id="answer"
                        name="answer"
                        value={formData.answer}
                        onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                        placeholder="Start writing your content here. Use natural language and bullet points as needed..."
                        className={cn(
                          "w-full text-base sm:text-lg font-light text-gray-800 placeholder-gray-400 bg-transparent border-0 resize-none",
                          "focus:outline-none focus:ring-0 px-0 py-3 leading-relaxed",
                          "hover:text-gray-900 focus:text-gray-900 transition-colors",
                          validationErrors.answer && "text-red-700 placeholder-red-300"
                        )}
                        style={{ 
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          lineHeight: '1.6',
                          minHeight: '300px'
                        }}
                        rows={Math.max(12, Math.min(25, (formData.answer.match(/\n/g) || []).length + 12))}
                        disabled={isSubmitting}
                      />
                      {validationErrors.answer && (
                        <motion.p 
                          className="text-xs text-red-500 flex items-center space-x-1"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                        >
                          <span>⚠</span>
                          <span>{validationErrors.answer}</span>
                        </motion.p>
                      )}
                    </motion.div>

                    {/* NEW: Qualifiers Section */}
                    {accountId && answer?.settings_id && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="space-y-4 pt-4 sm:pt-6 border-t border-gray-100"
                      >
                        <BrainSettingConditions
                          settingId={answer.settings_id}
                          accountId={accountId}
                          initialRequirements={requirements}
                          onRequirementsChange={(newReqs) => {
                            setRequirements(newReqs);
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </motion.div>
                    )}

                    {/* Info message when hiding content tab */}
                  </div>

                  {/* 🎨 BRANDED DOCUMENT FOOTER */}
                  <div className="px-8 sm:px-12 py-4 bg-gray-50/30 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      {/* Brand Attribution */}
                      <div className="text-xs text-gray-400">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded bg-blue-500"></div>
                          <span>Powered by <span className="font-medium">The Savants</span></span>
                        </div>
                      </div>

                      {/* Document Info */}
                      <div className="text-right text-xs">
                        <div className="font-medium text-gray-700">
                          {answer ? 'Knowledge Document' : 'New Knowledge Document'}
                        </div>
                        <div className="text-gray-500 mt-0.5">
                          {accountData.updatedAt ? (
                            `Last Update: ${new Date(accountData.updatedAt).toLocaleString('en-US', { 
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })} on ${new Date(accountData.updatedAt).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric'
                            })}`
                          ) : (
                            `Created: ${new Date().toLocaleString('en-US', { 
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })} on ${new Date().toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric'
                            })}`
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      ) : (
        // 🎨 Modal mode - Microsoft Word document style with branding
        <>
          {/* 🏢 BRANDED COMPANY LETTERHEAD WITH TITLE */}
          <motion.div 
            className="bg-white border-b border-gray-100/50 py-3 px-6 sm:px-8 relative"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            {/* Brand Stripe */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600"></div>

            {/* Company Letterhead */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Company Logo/Icon */}
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <BookOpen size={16} className="text-white" />
                  </div>
                  <div>
                    {/* Document Title in Header */}
                    <input
                      id="question"
                      name="question"
                      ref={titleRef}
                      value={formData.question}
                      onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                      placeholder="Untitled document"
                      className={cn(
                        "font-medium text-base sm:text-lg text-gray-900 placeholder-gray-400 bg-transparent border-0",
                        "focus:outline-none focus:ring-0 px-0 py-1 transition-colors w-full min-w-[300px] max-w-[500px]",
                        "hover:bg-gray-50/50 focus:bg-gray-50/50 rounded-md px-2",
                        validationErrors.question && "text-red-700 placeholder-red-300"
                      )}
                      style={{ 
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        lineHeight: '1.3'
                      }}
                      disabled={isSubmitting}
                    />
                    {/* Category Selection below title */}
                    <Select 
                      value={formData.category_id} 
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-6 text-[10px] sm:text-xs text-gray-500 border-0 bg-transparent hover:bg-gray-50 focus:bg-gray-50 rounded-md px-2 py-1 mt-0.5",
                          "focus:ring-0 focus:outline-none shadow-none w-full min-w-0",
                          validationErrors.category_id && "text-red-500"
                        )}
                      >
                        <SelectValue placeholder="Choose category..." />
                      </SelectTrigger>
                      <SelectContent className="z-[10060] bg-white border border-gray-200 rounded-lg shadow-lg">
                        {categories.map(category => (
                          <SelectItem 
                            key={category.id} 
                            value={category.id} 
                            className="text-sm py-2.5 hover:bg-gray-50 focus:bg-blue-50 cursor-pointer"
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="new" className="text-sm text-blue-600 py-2.5 hover:bg-blue-50 focus:bg-blue-100 cursor-pointer border-t border-gray-100">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-lg bg-blue-500 flex items-center justify-center mr-2">
                              <Plus className="h-2.5 w-2.5 text-white" />
                            </div>
                            Create new category
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {validationErrors.category_id && (
                      <div className="text-xs text-red-500 mt-1">
                        {validationErrors.category_id}
                      </div>
                    )}
                    {validationErrors.question && (
                      <div className="text-xs text-red-500 mt-1">
                        {validationErrors.question}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Badge and Close */}
              <div className="flex items-center gap-4 sm:gap-6">
                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCloseAttempt}
                  className="rounded-full h-7 w-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X size={14} />
                </motion.button>
              </div>
            </div>

            {/* Header Separator */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200"></div>
          </motion.div>

          {/* Chrome-style Location Variant Tabs - NOW conditional on active variants */}
          {hasActiveVariants && <LocationVariantTabs />}

          {/* Document Paper Container */}
          <div className="flex-1 bg-gradient-to-br from-blue-50/30 via-gray-50/20 to-blue-50/40 overflow-y-auto">
            {/* Loading Skeleton */}
            {modalIsLoading && (
              <motion.div 
                className="flex items-center justify-center py-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Loading document...</p>
                </div>
              </motion.div>
            )}

            {/* Document Sheet */}
            {!modalIsLoading && (
              <motion.div 
                className="min-h-full py-8 px-4 flex justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {/* Paper Sheet */}
                <div className="w-full max-w-2xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.08)] rounded-sm border border-gray-200/50 overflow-hidden">
                  {/* Document Content */}
                  <div className="px-4 sm:px-8 md:px-12 py-6 sm:py-8 md:py-10 space-y-4 sm:space-y-6 md:space-y-8">
                    {/* Document Content */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                        <label className="text-sm font-light text-gray-700">
                          Content <span className="text-red-400 ml-1">*</span>
                        </label>
                      </div>
                      <textarea
                        id="answer"
                        name="answer"
                        value={formData.answer}
                        onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                        placeholder="Start writing your content here. Use natural language and bullet points as needed..."
                        className={cn(
                          "w-full text-base font-light text-gray-800 placeholder-gray-400 bg-transparent border-0 resize-none",
                          "focus:outline-none focus:ring-0 px-0 py-3 leading-relaxed",
                          "hover:text-gray-900 focus:text-gray-900 transition-colors",
                          validationErrors.answer && "text-red-700 placeholder-red-300"
                        )}
                        style={{ 
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          lineHeight: '1.6',
                          minHeight: '200px'
                        }}
                        rows={Math.max(8, Math.min(15, (formData.answer.match(/\n/g) || []).length + 8))}
                        disabled={isSubmitting}
                      />
                      {validationErrors.answer && (
                        <motion.p 
                          className="text-xs text-red-500 flex items-center space-x-1"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                        >
                          <span>⚠</span>
                          <span>{validationErrors.answer}</span>
                        </motion.p>
                      )}
                    </motion.div>

                    {/* NEW: Qualifiers Section for Modal view */}
                    {accountId && answer?.settings_id && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-4 pt-4 sm:pt-6 border-t border-gray-100"
                      >
                        <BrainSettingConditions
                          settingId={answer.settings_id}
                          accountId={accountId}
                          initialRequirements={requirements}
                          onRequirementsChange={(newReqs) => {
                            setRequirements(newReqs);
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </motion.div>
                    )}

                    {/* Info message when hiding content tab */}
                  </div>

                  {/* 🎨 BRANDED DOCUMENT FOOTER */}
                  <div className="px-12 py-6 bg-gray-50/50 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      {/* Left side with Brand and Doc Info */}
                      <div className="flex items-center gap-6">
                        {/* Brand Attribution */}
                        <div className="text-xs text-gray-400">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded bg-blue-500"></div>
                            <span>Powered by <span className="font-medium">The Savants</span></span>
                          </div>
                        </div>

                        {/* Document Info */}
                        <div className="text-left text-xs hidden sm:block">
                          <div className="font-medium text-gray-700">
                            {answer ? 'Knowledge Document' : 'New Knowledge Document'}
                          </div>
                          <div className="text-gray-500 mt-0.5">
                            {accountData.updatedAt ? (
                              `Last Update: ${new Date(accountData.updatedAt).toLocaleString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} on ${new Date(accountData.updatedAt).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric'
                              })}`
                            ) : (
                              `Created: ${new Date().toLocaleString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} on ${new Date().toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric'
                              })}`
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleCloseAttempt}
                          className="py-2 px-4 rounded-lg border border-gray-200 text-gray-700 text-sm
                            bg-white hover:bg-gray-50 transition-colors"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: hasUnsavedChanges || !formData.settings_id ? 1.02 : 1 }}
                          whileTap={{ scale: hasUnsavedChanges || !formData.settings_id ? 0.98 : 1 }}
                          onClick={handleSubmit}
                          disabled={isSubmitting || (formData.settings_id && !hasUnsavedChanges)}
                          className={cn(
                            "py-2 px-5 rounded-lg text-sm font-medium transition-colors",
                            (hasUnsavedChanges || !formData.settings_id) 
                              ? "bg-blue-500 hover:bg-blue-600 text-white" 
                              : "bg-gray-200 text-gray-500 cursor-not-allowed",
                            "disabled:opacity-50 disabled:cursor-not-allowed"
                          )}
                        >
                          {isSubmitting ? (
                            <div className="flex items-center">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                              />
                              Saving...
                            </div>
                          ) : (
                            <span>Save</span>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );

  if (isInline) {
    return(
      <>
        {renderContent()}
        <LocationVariantsModal
          isOpen={isLocationModalOpen}
          onClose={() => setIsLocationModalOpen(false)}
          onSave={handleLocationVariantsChange}
          supabase={supabase}
          locations={fetchedLocations}
          settings_id={answer?.settings_id}
          setting={answer}
        />
      </>
    );
  }

// Use createPortal for modal mode, similar to RoadsPopup
const modalContent = (
  <AnimatePresence>
      <motion.div 
        initial="hidden"
        animate="visible"
        exit="exit"
      variants={backdropVariants}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={handleCloseAttempt}
      >
        {renderContent()}
      </motion.div>
  </AnimatePresence>
);

return (
  <>
    {/* Modal */}
    {createPortal(modalContent, document.body)}

      {/* Unsaved Changes Modal */}
      <ConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={() => {
          setIsUnsavedChangesModalOpen(false);
          onClose('discard');
        }}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard Changes"
        confirmStyle="bg-red-600 hover:bg-red-700"
        cancelText="Continue Editing"
      />
    </>
  );
};

export default SimpleAnswerModal; 